/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import Head from "next/head";
import Link from "next/link";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../../components/banner";
import Newsletter from "../../components/newsletter";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

class Index extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/products/candles", market);
    const alt = "Lasting Smiles";
    const title = "Aromatherapy Cause Candles · Lasting Smiles";
    const description = "";

    const list = {
      hidden: {},
      visible: {
        transition: {
          delay: 0.25,
          staggerChildren: 0.1,
          when: "beforeChildren",
        },
      },
    };

    const item = {
      hidden: {
        opacity: 0,
        scale: 0,
        y: 50,
      },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
      },
    };

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/products/candles", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/candles", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/products/candles", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products", market),
                    "name": "Products",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/products/candles", market),
                    "name": "Candles",
                    "position": 2,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <h1 className="font-medium font-serif text-3xl text-center">
            Lasting&nbsp;Smiles Aromatherapy Cause&nbsp;Candles
          </h1>
          <p className="text-center text-xl">
            Adding elevated warmth to any environment
          </p>
          <motion.div
            animate="visible"
            className="flex flex-row flex-wrap items-center justify-around"
            initial="hidden"
            variants={list}
          >
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/balance-aromatherapy-cause-candle", market)}
                href={{
                  pathname: "/products/balance-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/balance-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/balance-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Balance - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../../public/images/products/balance-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Balance
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/bliss-aromatherapy-cause-candle", market)}
                href={{
                  pathname: "/products/bliss-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/bliss-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/bliss-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Bliss - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../../public/images/products/bliss-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Bliss
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/calm-aromatherapy-cause-candle", market)}
                href={{
                  pathname: "/products/calm-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/calm-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/calm-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Calm - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../../public/images/products/calm-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Calm
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/focus-aromatherapy-cause-candle", market)}
                href={{
                  pathname: "/products/focus-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/focus-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/focus-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Focus - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../../public/images/products/focus-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Focus
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/tortoise-handblown-glass-candle", market)}
                href={{
                  pathname: "/products/tortoise-handblown-glass-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/tortoise-handblown-glass-candle/life1.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/tortoise-handblown-glass-candle/life1.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Tortoise Handblown Glass Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../../public/images/products/tortoise-handblown-glass-candle/life1.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Tortoise Handblown Glass
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/ribbed-glass-candle", market)}
                href={{
                  pathname: "/products/ribbed-glass-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/ribbed-glass-candle/life2.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/ribbed-glass-candle/life2.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Ribbed Glass Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../../public/images/products/ribbed-glass-candle/life2.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Ribbed Glass
                  </span>
                </a>
              </Link>
            </motion.div>
            <motion.div
              className="flex-initial m-5 text-center"
              variants={item}
            >
              <Link
                as={toRelativePageURL("/products/sea-salt-citrus-aromatherapy-cause-candle", market)}
                href={{
                  pathname: "/products/sea-salt-citrus-aromatherapy-cause-candle",
                  query: { market },
                }}
              >
                <a>
                  <picture>
                    <source
                      srcSet={require("../../public/images/products/sea-salt-citrus-aromatherapy-cause-candle/box.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/products/sea-salt-citrus-aromatherapy-cause-candle/box.jpg") as string}
                      type="image/jpeg"
                    />
                    <motion.img
                      alt="Sea Salt &amp; Citrus - Aromatherapy Cause Candle by Lasting Smiles"
                      className="rounded"
                      src={require("../../public/images/products/sea-salt-citrus-aromatherapy-cause-candle/box.jpg") as string}
                      style={{ maxWidth: 200 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    />
                  </picture>
                  <br />
                  <span className="border-b-2 border-black font-medium inline-block uppercase">
                    Sea Salt &amp; Citrus
                  </span>
                </a>
              </Link>
            </motion.div>
          </motion.div>
        </section>
      </>
    );
  }
}

export default Index;
